/**
 * 商行健康度详情页
 * 通过 URL /statics/stallHealth/detail/:id 访问，根据 id 展示商行基本信息、总健康度得分及健康度明细
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Row, Col, Progress, Tooltip, Spin } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Card } from '@/components/index';
import './stallHealthDetail.less';
import { getHealthLevelByLevel } from '.';
import tipIcon from '@/common/images/tip.png';
import { getCxmHealthStatisticDetailRequest } from '@/state/request/cxmHealthStatistic';
import { BUSINESS_TYPE, STALL_SALE_TYPE, STALL_TYPE, STALL_TYPES } from '@/utils/deviceType';
import dayjs from 'dayjs';
import rightPng from '@/common/images/right.png';

// 健康度绿色（与列表页一致）
const COLOR_HEALTHY = '#6DDE96';
const COLOR_ORANGE = '#FFBF81';

const healthTip = [
  <span>
    <span>0≤开单天数≤1，0分</span>
    <br />
    <span>1＜开单天数≤3，30分</span>
    <br />
    <span>3＜开单天数≤5，70分</span>
    <br />
    <span>5＜开单天数≤7，100分</span>
  </span>,
  <span>
    <span>0≤分享账单天数≤1，0分</span>
    <br />
    <span>1＜分享账单天数≤3，30分</span>
    <br />
    <span>3＜分享账单天数≤5，70分</span>
    <br />
    <span>5＜分享账单天数≤7，100分</span>
  </span>,
  <span>
    <span>无买家还款，0分</span>
    <br />
    <span>0＜产生过买家还款天数≤3，50分</span>
    <br />
    <span>3＜产生过买家还款天数≤7，100分</span>
  </span>,
  <span>
    <span>无库存操作，0分</span>
    <br />
    <span>0＜产生过库存操作天数≤3，50分</span>
    <br />
    <span>3＜产生过库存操作天数≤7，100分</span>
  </span>,
  <span>
    <span>无货主付款，0分</span>
    <br />
    <span>有过货主付款，100分</span>
  </span>,
];

const StallHealthDetail = () => {
  const history = useHistory();
  const { id } = useParams();
  const [stallInfo, setStallInfo] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      const res = await getCxmHealthStatisticDetailRequest(id);
      setLoading(false);
      if (res.code === 200) {
        setStallInfo(res.data);
      }
    };
    fetchDetail();
  }, [id]);
  const healthLevel = getHealthLevelByLevel(stallInfo?.healthLevel);
  const { tagStyle = {}, label } = healthLevel || {};
  const healthMetrics = useMemo(() => {
    return [
      {
        label: '开单活跃',
        weight: stallInfo?.createOrder,
        description: `近7天开单${stallInfo?.orderCount || 0}天`,
        score: stallInfo?.orderScore,
      },
      {
        label: '分享账单',
        weight: stallInfo?.shareOrder,
        description: `近7天分享${stallInfo?.orderShare || 0}天`,
        score: stallInfo?.orderShareScore,
      },
      {
        label: '买家还款',
        weight: stallInfo?.repayment,
        description: `近7天还款${stallInfo?.repayCount || 0}天`,
        score: stallInfo?.repayScore,
      },
      {
        label: '库存管理',
        weight: stallInfo?.stockOperation,
        description: `近7天操作过${stallInfo?.stockCount || 0}天`,
        score: stallInfo?.stockScore,
      },
      {
        label: '货主付款',
        weight: stallInfo?.supplierPay,
        description: `近7天${stallInfo?.supplierPayCount > 0 ? '有过' : '无'}付款`,
        score: stallInfo?.supplierPayScore,
      },
    ];
  }, [stallInfo]);
  const handleTitleClick = () => {
    history.push({
      pathname: '/booth/stallManager/stallManagementDetail',
      state: { stallId: stallInfo.stallId, from: 'GO_BACK' },
    });
  };
  return (
    <Spin spinning={loading}>
      <PageHeaderLayout>
        <div className='stall-health-detail'>
          {/* 顶部卡片：商行基本信息与总健康度 */}
          <Card bordered={false} className='stall-health-detail-card'>
            <Row gutter={24} wrap={false}>
              <Col flex='auto'>
                <h2 className='stall-health-detail-title' onClick={handleTitleClick}>
                  {stallInfo?.businessCode} {stallInfo?.stallName}
                  <img src={rightPng} alt='right' className='stall-health-detail-title-right' />
                </h2>
                <Row gutter={[24, 8]} className='stall-health-detail-info'>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>城市：</span>
                    <span className='info-value'>{`${stallInfo?.provinceName || ''} ${
                      stallInfo?.cityName || ''
                    } `}</span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>市场名称：</span>
                    <span className='info-value'>{stallInfo?.marketName}</span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>经营品类：</span>
                    <span className='info-value'>
                      {BUSINESS_TYPE[stallInfo?.businessType] || '--'}
                    </span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>商行版本：</span>
                    <span className='info-value'>{STALL_TYPE.get(stallInfo?.type) || '--'}</span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>签约日期：</span>
                    <span className='info-value'>
                      {stallInfo?.pactStartTime
                        ? dayjs(stallInfo?.pactStartTime).format('YYYY-MM-DD')
                        : '--'}
                    </span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>到期日期：</span>
                    <span className='info-value'>
                      {stallInfo?.serviceEndTime
                        ? dayjs(stallInfo?.serviceEndTime).format('YYYY-MM-DD')
                        : '--'}
                    </span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>商行分类：</span>
                    <span className='info-value'>
                      {STALL_TYPES.get(stallInfo?.pactStatus) || '--'}
                    </span>
                  </Col>
                  <Col xs={12} sm={8}>
                    <span className='info-label'>管理模式：</span>
                    <span className='info-value'>
                      {STALL_SALE_TYPE.get(stallInfo?.saleType) || '--'}
                    </span>
                  </Col>
                </Row>
              </Col>
              <Col flex='none'>
                <div
                  className='stall-health-detail-badge'
                  style={{
                    borderColor: tagStyle.borderColor,
                    color: tagStyle.color,
                    backgroundImage: tagStyle.bgImg,
                  }}
                >
                  <span className='badge-score'>{`${stallInfo?.healthScore || 0}分`}</span>
                  <span className='badge-status'>{label || '--'}</span>
                </div>
              </Col>
            </Row>
          </Card>

          {/* 底部卡片：健康度明细 */}
          <Card bordered={false} className='stall-health-detail-card stall-health-detail-metrics'>
            <h3 className='stall-health-detail-section-title'>
              健康度明细
              <span>
                统计时间：
                {stallInfo?.startTime
                  ? `${dayjs(stallInfo?.startTime).format('YYYY-MM-DD')} 至 ${dayjs(
                      stallInfo?.endTime,
                    ).format('YYYY-MM-DD')}`
                  : '--'}
              </span>
            </h3>
            <div className='stall-health-detail-metrics-list'>
              {healthMetrics.map((item, index) => (
                <div key={item.id} className='stall-health-detail-metric-item'>
                  <div className='metric-header'>
                    <span
                      className='metric-label'
                      style={
                        item.label === '开单活跃'
                          ? { cursor: 'pointer', display: 'flex', alignItems: 'center' }
                          : {}
                      }
                      onClick={() => {
                        if (item.label === '开单活跃') {
                          history.push({
                            pathname: '/statics/billStatistics',
                            state: { businessCode: stallInfo.businessCode },
                          });
                        }
                      }}
                    >
                      {item.label}
                      {item.label === '开单活跃' && (
                        <img
                          src={rightPng}
                          alt='right'
                          className='metric-label-right'
                          style={{ marginLeft: 2, width: 16, height: 16 }}
                        />
                      )}
                    </span>
                    <Tooltip title={healthTip[index]}>
                      <img src={tipIcon} alt='tip' className='metric-info-icon' />
                    </Tooltip>
                    (权重{item.weight}%)
                  </div>
                  <div className='metric-progress-row'>
                    <Progress
                      percent={item.score}
                      showInfo={false}
                      strokeColor={item.score > 50 ? COLOR_HEALTHY : COLOR_ORANGE}
                      trailColor='#F0F0F0'
                      className='metric-progress'
                    />
                    <span
                      className='metric-score'
                      style={{
                        color: item.score > 50 ? '#181818' : '#FF8003',
                        fontSize: 16,
                        fontWeight: 500,
                      }}
                    >
                      {item.score}分
                    </span>
                  </div>
                  <div className='metric-desc'>{item.description}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </PageHeaderLayout>
    </Spin>
  );
};

export default StallHealthDetail;
